import React from 'react';

interface SparklineSVGProps {
  data: number[];
  positive: boolean;
  width?: number;
  height?: number;
  className?: string;
}

/**
 * Attempt a monotone cubic interpolation for a smooth sparkline.
 * Returns an SVG path "d" attribute for the given points.
 */
function buildSmoothPath(
  points: { x: number; y: number }[]
): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M${points[0].x},${points[0].y}L${points[1].x},${points[1].y}`;
  }

  // Compute tangent slopes using monotone cubic (Fritsch-Carlson)
  const n = points.length;
  const dx: number[] = [];
  const dy: number[] = [];
  const m: number[] = [];

  for (let i = 0; i < n - 1; i++) {
    dx.push(points[i + 1].x - points[i].x);
    dy.push(points[i + 1].y - points[i].y);
    m.push(dy[i] / dx[i]);
  }

  const tangents: number[] = [m[0]];
  for (let i = 1; i < n - 1; i++) {
    if (m[i - 1] * m[i] <= 0) {
      tangents.push(0);
    } else {
      tangents.push((m[i - 1] + m[i]) / 2);
    }
  }
  tangents.push(m[n - 2]);

  // Build cubic bezier segments
  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < n - 1; i++) {
    const seg = dx[i] / 3;
    const cp1x = points[i].x + seg;
    const cp1y = points[i].y + tangents[i] * seg;
    const cp2x = points[i + 1].x - seg;
    const cp2y = points[i + 1].y - tangents[i + 1] * seg;
    d += `C${cp1x},${cp1y},${cp2x},${cp2y},${points[i + 1].x},${points[i + 1].y}`;
  }

  return d;
}

let sparkIdCounter = 0;

export function SparklineSVG({
  data,
  positive,
  width = 60,
  height = 24,
  className,
}: SparklineSVGProps) {
  const idRef = React.useRef(`spark-${++sparkIdCounter}`);
  const gradId = `${idRef.current}-g`;
  const filterId = `${idRef.current}-f`;

  const lineColor = positive ? '#4ADE80' : '#F87171';

  // Padding for endpoint dot
  const padX = 5;
  const padY = 4;
  const plotW = width - padX * 2;
  const plotH = height - padY * 2;

  // Handle < 2 data points: render flat line
  const safeData = data.length < 2 ? [0, 0] : data;
  const minVal = Math.min(...safeData);
  const maxVal = Math.max(...safeData);
  const range = maxVal - minVal || 1;

  const points = safeData.map((v, i) => ({
    x: padX + (i / (safeData.length - 1)) * plotW,
    y: padY + (1 - (v - minVal) / range) * plotH,
  }));

  const linePath = buildSmoothPath(points);
  const lastPoint = points[points.length - 1];

  // Area path: line path + close at bottom
  const areaPath = `${linePath}L${points[points.length - 1].x},${height}L${points[0].x},${height}Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      fill="none"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity={0.3} />
          <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
        </linearGradient>
        <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="0" stdDeviation="1.5" floodColor={lineColor} floodOpacity="0.5" />
        </filter>
      </defs>

      {/* Gradient fill area */}
      <path d={areaPath} fill={`url(#${gradId})`} />

      {/* Line */}
      <path d={linePath} stroke={lineColor} strokeWidth={1.5} strokeLinecap="round" />

      {/* Endpoint dot: outer ring + inner circle with drop shadow */}
      <circle
        cx={lastPoint.x}
        cy={lastPoint.y}
        r={4}
        fill={lineColor}
        opacity={0.3}
        filter={`url(#${filterId})`}
      />
      <circle
        cx={lastPoint.x}
        cy={lastPoint.y}
        r={2}
        fill={lineColor}
      />
    </svg>
  );
}
